<template>
  <div class="dailypayouts-page">
    <div class="page-header">
      <div class="page-avatar"><v-icon size="22" color="#fff">mdi-cash-clock</v-icon></div>
      <div class="page-header-text">
        <h1 class="page-title">{{ source === 'UBER' ? $t('uberDailyPayouts.title') : $t('dailyPayouts.title') }}</h1>
      </div>
      <v-btn-toggle v-model="source" mandatory dense dark>
        <v-btn value="BOLT">
          <v-icon small left>mdi-taxi</v-icon> Bolt
        </v-btn>
        <v-btn value="UBER">
          <v-icon small left>mdi-car-sports</v-icon> Uber
        </v-btn>
        <v-btn value="FREENOW">
          <v-icon small left>mdi-car</v-icon> FreeNow
        </v-btn>
      </v-btn-toggle>
    </div>

    <v-alert v-if="source === 'FREENOW'" type="info" outlined class="mb-4">
      <v-icon small left>mdi-clock-outline</v-icon>
      {{ $t('dailyPayouts.freeNowComingSoon') }}
    </v-alert>

    <v-alert v-if="source !== 'FREENOW' && notConfigured" type="info" outlined class="mb-4">
      {{ $t('dailyPayouts.configureBoltFirst') }}
      <v-btn small text color="primary" to="/system-settings" class="ml-2">
        <v-icon small left>mdi-cog</v-icon>{{ $t('nav.systemSettings') }}
      </v-btn>
    </v-alert>

    <v-alert v-if="!notConfigured" type="info" outlined dense class="mb-4">
      <v-icon small left>mdi-information-outline</v-icon>
      {{ $t('dailyPayouts.integrationHint') }}
    </v-alert>

    <v-card v-if="!notConfigured" outlined class="mb-4" style="background: var(--bg-surface); border-color: var(--border-default);">
      <v-card-text class="d-flex align-center" style="gap: 12px;">
        <v-text-field
          v-model="date"
          :label="$t('dailyPayouts.date')"
          type="date"
          outlined
          dense
          dark
          hide-details
          style="max-width: 220px;"
        />
        <v-btn outlined color="info" @click="load" :loading="loading">
          <v-icon left>mdi-refresh</v-icon>{{ $t('dailyPayouts.refresh') }}
        </v-btn>
        <v-spacer />
        <v-chip v-if="result" small outlined color="info" class="mr-1">
          {{ $t('dailyPayouts.totalOrders') }}: {{ result.totalOrders }}
        </v-chip>
        <v-chip v-if="result" small outlined :color="result.unmatchedDrivers ? 'warning' : 'success'" class="mr-1">
          {{ $t('dailyPayouts.matchedDrivers') }}: {{ result.matchedDrivers }}
        </v-chip>
        <v-chip v-if="result && result.unmatchedDrivers" small outlined color="warning">
          {{ $t('dailyPayouts.unmatchedDrivers') }}: {{ result.unmatchedDrivers }}
        </v-chip>
        <v-btn color="primary" @click="generate" :loading="generating"
               :disabled="!hasGeneratable" class="ml-3">
          <v-icon left>mdi-send</v-icon>{{ $t('dailyPayouts.generate') }}
        </v-btn>
      </v-card-text>
    </v-card>

    <v-alert v-if="source === 'UBER' && unmappedCategoriesSeen.length" type="warning" outlined dense class="mb-3">
      <div class="font-weight-bold mb-1">{{ $t('uberDailyPayouts.unmappedWarning') }}:</div>
      <div class="caption"><code>{{ unmappedCategoriesSeen.join(', ') }}</code></div>
    </v-alert>

    <v-alert v-if="result && result.unmatched.length" type="warning" outlined dense class="mb-3">
      <div class="font-weight-bold mb-1">{{ $t('dailyPayouts.unmatchedWarning') }}:</div>
      <div v-for="u in result.unmatched" :key="u.driverUuid" class="caption">
        · {{ u.driverName }} ({{ u.ordersCount }} {{ $t('dailyPayouts.ordersCount').toLowerCase() }})
        — <code>{{ u.driverUuid }}</code>
      </div>
    </v-alert>

    <v-card v-if="result" outlined style="background: var(--bg-surface); border-color: var(--border-default);">
      <v-data-table
        :headers="headers"
        :items="result.drivers"
        :items-per-page="50"
        :footer-props="{ itemsPerPageOptions: [20, 50, 100, -1] }"
        dark
        dense
      >
        <template v-slot:[`item.driverName`]="{ item }">
          <span :class="{ 'grey--text': !item.matched }">{{ item.driverName }}</span>
        </template>
        <template v-slot:[`item.grossTotal`]="{ item }">{{ fmt(item.grossTotal) }}</template>
        <template v-slot:[`item.cashCollected`]="{ item }">{{ fmt(item.cashCollected) }}</template>
        <template v-slot:[`item.tips`]="{ item }">{{ fmt(item.tips) }}</template>
        <template v-slot:[`item.commissionBolt`]="{ item }">{{ fmt(item.commissionBolt) }}</template>
        <template v-slot:[`item.netEarnings`]="{ item }">{{ fmt(item.netEarnings) }}</template>
        <template v-slot:[`item.calculatedAmount`]="{ item }">
          <span v-if="item.calculatedAmount != null">{{ fmt(item.calculatedAmount) }}</span>
          <span v-else class="grey--text">—</span>
        </template>
        <template v-slot:[`item.dailyPayoutFee`]="{ item }">{{ fmt(item.dailyPayoutFee) }}</template>
        <template v-slot:[`item.finalTransferAmount`]="{ item }">
          <strong v-if="item.finalTransferAmount != null" class="success--text">{{ fmt(item.finalTransferAmount) }}</strong>
          <span v-else class="grey--text">—</span>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip x-small v-if="!item.matched" color="grey darken-2">{{ $t('dailyPayouts.statusUnmatched') }}</v-chip>
          <v-chip x-small v-else-if="item.alreadyPaid" color="info">{{ $t('dailyPayouts.statusAlreadyPaid') }}</v-chip>
          <v-tooltip v-else-if="item.formulaError" top>
            <template v-slot:activator="{ on }">
              <v-chip x-small v-on="on" color="warning">{{ $t('dailyPayouts.statusFormulaError') }}</v-chip>
            </template>
            <span>{{ item.formulaError }}</span>
          </v-tooltip>
          <v-chip x-small v-else-if="item.finalTransferAmount == null || item.finalTransferAmount <= 0" color="grey">
            {{ $t('dailyPayouts.statusNoAmount') }}
          </v-chip>
          <v-chip x-small v-else color="success">{{ $t('dailyPayouts.statusReady') }}</v-chip>
        </template>
      </v-data-table>
    </v-card>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000" top>
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
const axios = require('axios');

function yesterdayIso() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

export default {
  name: 'DailyPayoutsPage',
  data: () => ({
    date: yesterdayIso(),
    source: 'BOLT',
    result: null,
    loading: false,
    generating: false,
    notConfigured: false,
    snackbar: { show: false, color: 'success', text: '' },
  }),
  computed: {
    apiPrefix() { return this.source === 'UBER' ? 'uber' : 'bolt'; },
    headers() {
      if (this.source === 'UBER') {
        return [
          { text: this.$t('dailyPayouts.driver'), value: 'driverName' },
          { text: this.$t('dailyPayouts.grossTotal'), value: 'earnings', align: 'end' },
          { text: this.$t('dailyPayouts.cashCollected'), value: 'cashCollected', align: 'end' },
          { text: this.$t('dailyPayouts.tips'), value: 'tips', align: 'end' },
          { text: this.$t('dailyPayouts.commission'), value: 'commission', align: 'end' },
          { text: this.$t('dailyPayouts.calculatedAmount'), value: 'calculatedAmount', align: 'end' },
          { text: this.$t('dailyPayouts.fee'), value: 'dailyPayoutFee', align: 'end' },
          { text: this.$t('dailyPayouts.finalAmount'), value: 'finalTransferAmount', align: 'end' },
          { text: this.$t('dailyPayouts.status'), value: 'status', sortable: false },
        ];
      }
      return [
        { text: this.$t('dailyPayouts.driver'), value: 'driverName' },
        { text: this.$t('dailyPayouts.ordersCount'), value: 'ordersCount', align: 'end' },
        { text: this.$t('dailyPayouts.grossTotal'), value: 'grossTotal', align: 'end' },
        { text: this.$t('dailyPayouts.cashCollected'), value: 'cashCollected', align: 'end' },
        { text: this.$t('dailyPayouts.tips'), value: 'tips', align: 'end' },
        { text: this.$t('dailyPayouts.commission'), value: 'commissionBolt', align: 'end' },
        { text: this.$t('dailyPayouts.netEarnings'), value: 'netEarnings', align: 'end' },
        { text: this.$t('dailyPayouts.calculatedAmount'), value: 'calculatedAmount', align: 'end' },
        { text: this.$t('dailyPayouts.fee'), value: 'dailyPayoutFee', align: 'end' },
        { text: this.$t('dailyPayouts.finalAmount'), value: 'finalTransferAmount', align: 'end' },
        { text: this.$t('dailyPayouts.status'), value: 'status', sortable: false },
      ];
    },
    hasGeneratable() {
      if (!this.result) return false;
      return this.result.drivers.some(
        (d) => d.matched && !d.alreadyPaid && d.finalTransferAmount != null && d.finalTransferAmount > 0,
      );
    },
    unmappedCategoriesSeen() {
      if (this.source !== 'UBER' || !this.result) return [];
      const all = new Set();
      for (const d of this.result.drivers) {
        (d.unmappedCategories || []).forEach((c) => all.add(c));
      }
      return Array.from(all);
    },
  },
  async mounted() {
    await this.checkConfigured();
    if (!this.notConfigured) await this.load();
  },
  watch: {
    async source() {
      this.result = null;
      await this.checkConfigured();
      if (!this.notConfigured) await this.load();
    },
  },
  methods: {
    authHeaders() {
      return { Authorization: 'Bearer ' + localStorage.getItem('token') };
    },
    apiUrl(p) { return process.env.VUE_APP_ROOT_API + p; },
    fmt(n) {
      if (n == null || Number.isNaN(n)) return '—';
      return n.toFixed(2);
    },
    async checkConfigured() {
      if (this.source === 'FREENOW') {
        this.notConfigured = true;
        return;
      }
      try {
        const res = await axios({ method: 'get', url: this.apiUrl(this.apiPrefix + '/status'), headers: this.authHeaders() });
        const d = (res.data && res.data.data) || {};
        this.notConfigured = !d.configured;
      } catch (e) {
        this.notConfigured = true;
      }
    },
    async load() {
      if (this.source === 'FREENOW') return;
      this.loading = true;
      try {
        const res = await axios({
          method: 'get',
          url: this.apiUrl(this.apiPrefix + '/daily-payouts'),
          params: { date: this.date },
          headers: this.authHeaders(),
        });
        this.result = (res.data && res.data.data) || null;
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message;
        this.snackbar = { show: true, color: 'error', text: msg };
        this.result = null;
      } finally {
        this.loading = false;
      }
    },
    async generate() {
      this.generating = true;
      try {
        const res = await axios({
          method: 'post',
          url: this.apiUrl(this.apiPrefix + '/daily-payouts/' + this.date + '/generate'),
          headers: this.authHeaders(),
          data: {},
        });
        const d = (res.data && res.data.data) || {};
        if (d.skipped > 0) {
          this.snackbar = {
            show: true, color: 'warning',
            text: this.$t('dailyPayouts.generatePartial', { created: d.created, skipped: d.skipped }),
          };
        } else {
          this.snackbar = {
            show: true, color: 'success',
            text: this.$t('dailyPayouts.generateSuccess', { count: d.created }),
          };
        }
        await this.load();
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message;
        this.snackbar = { show: true, color: 'error', text: msg };
      } finally {
        this.generating = false;
      }
    },
  },
};
</script>

<style scoped>
.dailypayouts-page { background: #181C28; min-height: calc(100vh - 56px); padding: 16px 16px 40px; margin: -24px; color: #fff; }
.page-header { display: flex; align-items: center; gap: 12px; padding: 4px 4px 18px; flex-wrap: wrap; }
.page-avatar { width: 44px; height: 44px; border-radius: 14px; background: linear-gradient(135deg, #2962FF 0%, #5478FF 100%); display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 6px 18px rgba(41,98,255,0.4); }
.page-eyebrow { font-size: 12px; color: #6E7691; font-weight: 500; text-transform: capitalize; }
.page-title { font-size: 22px; font-weight: 800; color: #fff; margin: 2px 0 0; letter-spacing: -0.02em; }
.page-header-text { flex: 1; min-width: 0; }

.dailypayouts-page >>> .v-card { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; box-shadow: none !important; color: #fff !important; }
.dailypayouts-page >>> .v-text-field--outlined fieldset, .dailypayouts-page >>> .v-select--outlined fieldset { border-color: rgba(255,255,255,0.1) !important; }
.dailypayouts-page >>> .v-text-field--outlined .v-input__slot, .dailypayouts-page >>> .v-select--outlined .v-input__slot { background: #181C28 !important; border-radius: 12px !important; }
.dailypayouts-page >>> .v-label { color: #6E7691 !important; }
.dailypayouts-page >>> .v-text-field input, .dailypayouts-page >>> .v-select__selection { color: #fff !important; }
.dailypayouts-page >>> .v-input__prepend-inner .v-icon { color: #6E7691 !important; }
.dailypayouts-page >>> .v-data-table { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; color: #fff !important; box-shadow: none !important; }
.dailypayouts-page >>> .v-data-table thead tr th { background: #1F2434 !important; color: #6E7691 !important; font-size: 11px !important; font-weight: 700 !important; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid rgba(255,255,255,0.06) !important; }
.dailypayouts-page >>> .v-data-table tbody tr { background: #232838 !important; }
.dailypayouts-page >>> .v-data-table tbody tr:hover { background: #2A3042 !important; }
.dailypayouts-page >>> .v-data-table tbody tr td { color: #fff !important; border-bottom: 1px solid rgba(255,255,255,0.04) !important; }
.dailypayouts-page >>> .v-data-footer { background: #1F2434 !important; color: #B0BEC5 !important; border-top: 1px solid rgba(255,255,255,0.06) !important; }
.dailypayouts-page >>> .v-btn:not(.v-btn--icon) { border-radius: 10px !important; text-transform: none !important; font-weight: 600 !important; }
.dailypayouts-page >>> .v-btn-toggle { background: #232838 !important; border: 1px solid rgba(255,255,255,0.08) !important; border-radius: 12px !important; }
.dailypayouts-page >>> .v-btn-toggle .v-btn { background: transparent !important; color: #B0BEC5 !important; }
.dailypayouts-page >>> .v-btn-toggle .v-btn--active { background: #2962FF !important; color: #fff !important; }
.dailypayouts-page >>> .v-chip { border-radius: 999px !important; font-weight: 700 !important; }
.dailypayouts-page >>> .grey--text { color: #B0BEC5 !important; }
.dailypayouts-page >>> .v-progress-linear { background: rgba(255,255,255,0.06) !important; }
.dailypayouts-page >>> .v-dialog .v-card { background: #232838 !important; }
</style>
